import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import { useAuth } from "../../context/AuthContext";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../../hooks/useHelpGuideNavigation";
import { getCampaignReportSummary } from "../../api/marketingCampaigns";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { getReportsHomePathForRoute } from "../../utils/reportRouteContext";
import { CAMPAIGN_GOALS } from "../../constants/marketingCampaigns";
import { REPORT_HELP } from "../../constants/reportHelpContent";
import "../ReportsSectionCompact.css";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDaysToIsoDate = (dateStr, deltaDays) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateISO(base, "UTC");
};

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

export default function MarketingCampaignsReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.MARKETING_CAMPAIGNS;
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const salonTimeZone = user?.salon_timezone || "America/Toronto";

  const todayIso = useMemo(() => formatDateISO(new Date(), salonTimeZone), [salonTimeZone]);
  const defaultStart = useMemo(() => addDaysToIsoDate(todayIso, -30), [todayIso]);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(todayIso);
  const [goal, setGoal] = useState("");
  const [status, setStatus] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runReport = useCallback(async () => {
    if (!salonId) return;
    if (isInvalidDateRange(startDate, endDate)) {
      setError("End date must be after start date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = {
        salon_id: salonId,
        start_date: startDate,
        end_date: endDate,
        goal: goal || null,
        status: status || null,
      };
      const data = await getCampaignReportSummary(payload, token);
      setSummary(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to run report.");
    } finally {
      setLoading(false);
    }
  }, [salonId, startDate, endDate, goal, status, token]);

  useEffect(() => {
    if (salonId) {
      runReport();
    }
  }, [salonId, runReport]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  return (
    <div className="reportSectionShell">
      <div className="reportSectionWide report-section-tight report-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Marketing Campaign Performance
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div
              id="marketing-campaigns-overview"
              className={getSectionClassName("marketing-campaigns-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div
              id="marketing-campaigns-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-3 ${getSectionClassName(
                "marketing-campaigns-filters"
              )}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Report filters
                  </div>
                  <div className="text-xs text-slate-500">
                    Salon: {salonName || "Unknown"} (ID {salonId || "--"})
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -7));
                      setEndDate(todayIso);
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 7 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -30));
                      setEndDate(todayIso);
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 30 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -90));
                      setEndDate(todayIso);
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 90 days
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Goal</label>
                  <select
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    <option value="">All goals</option>
                    {CAMPAIGN_GOALS.map((goalOption) => (
                      <option key={goalOption.value} value={goalOption.value}>
                        {goalOption.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="sending">Sending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={runReport}
                  className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white"
                  disabled={loading}
                >
                  {loading ? "Running..." : "Run report"}
                </button>
              </div>
            </div>

            <div
              id="marketing-campaigns-kpis"
              className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${getSectionClassName(
                "marketing-campaigns-kpis"
              )}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Total campaigns
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {summary?.total_campaigns || 0}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Total revenue
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {currency.format(Number(summary?.total_revenue || 0))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Total bookings
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {summary?.total_bookings || 0}
                </div>
              </div>
            </div>

            <div
              id="marketing-campaigns-details"
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                "marketing-campaigns-details"
              )}`}
            >
              <div className="text-sm font-semibold text-slate-900">Campaigns</div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Campaign</th>
                      <th className="px-3 py-2 text-left">Goal</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Sent</th>
                      <th className="px-3 py-2 text-left">Open rate</th>
                      <th className="px-3 py-2 text-left">Click rate</th>
                      <th className="px-3 py-2 text-left">Revenue</th>
                      <th className="px-3 py-2 text-left">Bookings</th>
                      <th className="px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(summary?.rows || []).map((row) => (
                      <tr key={row.campaign_id}>
                        <td className="px-3 py-2 text-slate-700">{row.name}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {CAMPAIGN_GOALS.find((g) => g.value === row.goal)?.label || row.goal}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{row.status}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.send_started_at
                            ? formatDateTimeInTimeZone(row.send_started_at, salonTimeZone)
                            : "--"}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {`${Math.round((row.open_rate || 0) * 100)}%`}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {`${Math.round((row.click_rate || 0) * 100)}%`}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {currency.format(Number(row.total_revenue || 0))}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {row.bookings_count || 0}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/manager/marketing/campaigns/${row.campaign_id}`)
                            }
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(summary?.rows || []).length === 0 && (
                      <tr>
                        <td
                          colSpan="9"
                          className="px-3 py-6 text-center text-xs text-slate-500"
                        >
                          No campaigns in this date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              id="marketing-campaigns-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "marketing-campaigns-export"
              )}`}
            >
              Export is not available on this report.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
